import React, { useState, ChangeEvent, useEffect } from 'react';
import { NavigateFunction } from 'react-router-dom';
import './NewUserForm.css';
import { IoClose } from 'react-icons/io5';
interface Role {
  roleId: number;
  roleName: string;
  roleDescription: string;
  accessLevel: number;
}
//TODO: (P1) Need to change these roles back to a network call on the Dashboard Page(user listing) instead of having them static here.
const STATIC_ROLES: Role[] = [
  {
    roleId: 1,
    roleName: 'Admin',
    roleDescription: 'Administrator',
    accessLevel: 10,
  },
  { roleId: 2, roleName: 'Lawyer', roleDescription: 'Lawyer', accessLevel: 5 },
  {
    roleId: 3,
    roleName: 'Physician',
    roleDescription: 'Reviewing physician',
    accessLevel: 2,
  },
  {
    roleId: 4,
    roleName: 'Patient',
    roleDescription: 'Patient to be presented',
    accessLevel: 1,
  },
  {
    roleId: 5,
    roleName: 'Super super Admin',
    roleDescription: 'Syd Med Team members',
    accessLevel: 100,
  },
];

interface NewUserFormData {
  firstName: string;
  middleName: string | null;
  lastName: string;
  userEmail: string;
  phone: string | null;
  displayName: string;
  roleId: number;
  companyId: number;
  formId: number;
}

interface Props {
  token: string;
  nav: NavigateFunction;
  handleCloseFormPressed: () => void;
  onUserCreated?: () => void;
}

// TODO Give some indicator that it is loading, if someone mashes the submit button that could cause issues.

function NewUserForm({
  token,
  nav,
  handleCloseFormPressed,
  onUserCreated,
}: Props): JSX.Element {
  const [formData, setFormData] = useState<NewUserFormData>({
    firstName: '',
    middleName: null,
    lastName: '',
    userEmail: '',
    phone: null,
    displayName: '',
    roleId: -1,
    companyId: -1, // TODO Putting temporary values as previous strings were in correct
    formId: -1, // @Anu please update this to be made correctly based on backend endpoint. I would prefer there to be dropdowns with the name of the form and company and using that to get the ID
  });
  const [roles] = useState<Role[]>(STATIC_ROLES);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchNewUserRoles = async () => {
      try {
        const response = await fetch('/admin/roles', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            nav('/signin');
            return;
          }
          throw new Error('Failed to fetch roles for new user');
        }
        const data = await response.json();
      } catch (error) {
        setError('Failed to load roles');
        console.log('Error fetching role list');
        console.log(error);
      }
    };

    fetchNewUserRoles();
  }, [token, nav]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const handleInputChange =
    (field: keyof NewUserFormData) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      if (error) setError('');
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value || null,
      }));
    };

  const handleRoleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (error) setError('');
    setFormData((prev) => ({
      ...prev,
      roleId: parseInt(event.target.value),
    }));
  };

  const getSelectedRoleName = (): string => {
    const selectedRole = roles.find((role) => role.roleId === formData.roleId);
    if (!selectedRole) {
      return '';
    }
    return selectedRole.roleName.toLowerCase();
  };

  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone) return true;

    const cleanPhone = phone.replace(/\D/g, '');
    const phoneNumber = Number(cleanPhone);
    return !isNaN(phoneNumber) && phoneNumber > 100_000_000;
  };

  const validateNewUserForm = (): boolean => {
    try {
      if (!formData.firstName.trim()) {
        setError('First name is required');
        return false;
      }
      if (!formData.lastName.trim()) {
        setError('Last name is required');
        return false;
      }
      if (!formData.userEmail.trim()) {
        setError('Email is required');
        return false;
      }
      if (!formData.displayName.trim()) {
        setError('Display name is required');
        return false;
      }
      if (formData.roleId === 0) {
        setError('Please select a role');
        return false;
      }

      if (formData.phone && !validatePhoneNumber(formData.phone)) {
        setError('Please enter a valid phone number');
        return false;
      }
      // Validate that the selected roleId exists in the available roles
      const selectedRole = roles.find(
        (role) => role.roleId === formData.roleId
      );
      if (!selectedRole) {
        setError('Please select a valid role');
        return false;
      }
      const selectedRoleName = getSelectedRoleName();
      if (selectedRoleName === 'patient') {
        if (!formData.companyId) {
          setError('Company ID is required for patients');
          return false;
        }
        if (!formData.formId) {
          setError('Form ID is required for patients');
          return false;
        }
      }
      return true;
    } catch (error) {
      setError('Invalid selected role');
      return false;
    }
  };

  const handleSubmitPressed = async () => {
    if (!validateNewUserForm()) return;

    const url = '/admin/addNewUser';
    try {
      const jsonData: NewUserFormData = {
        ...formData,
        middleName: formData.middleName == '' ? null : formData.middleName,
        phone: formData.phone,
      };

      const selectedRoleName = getSelectedRoleName();
      if (selectedRoleName === 'patient') {
        jsonData.companyId = 1; //TODO: Hardcoded companyId need to change it later when scaling
        jsonData.formId = 1; //TODO: Hardcoded formId need to change it later when scaling
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(jsonData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          nav('/signin');
          return;
        }
        if (response.status === 400) {
          setError('User already exists or invalid data provided');
          return;
        }
        throw new Error(`Network response was not ok: ${response.statusText}`);
      }
      onUserCreated?.();
      handleCloseFormPressed();
    } catch (err) {
      setError('Failed to create user. Please try again.');
      console.error('Error creating user:');
      console.error(err);
    }
  };

  return (
    <div className="new-user-form-overlay">
      <div className="new-user-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="new-user-form-header">
          <h2>Add New User</h2>
          <button
            className="new-user-close-button"
            onClick={handleCloseFormPressed}
            aria-label="Close"
          >
            <IoClose className="new-user-form-header-close" />
          </button>
        </div>

        <div className="new-user-form-content">
          <div className="new-user-form-container">
            {error && (
              <div>
                <p className="new-user-form-error">{error}</p>
              </div>
            )}

            <div className="new-user-form-row">
              <span className="new-user-form-label">User Role</span>
              <select value={formData.roleId} onChange={handleRoleChange}>
                <option value={0}>Select Role</option>
                {roles.map((role: Role) => (
                  <option key={role.roleId} value={role.roleId}>
                    {role.roleName}
                  </option>
                ))}
              </select>
            </div>

            <div className="new-user-form-name">
              <div className="new-user-form-row">
                <span className="new-user-form-label">First Name</span>
                <input
                  className="new-form-input-name"
                  type="text"
                  value={formData.firstName}
                  placeholder="Enter first name"
                  onChange={handleInputChange('firstName')}
                />
              </div>

              <div className="new-user-form-row">
                <span className="new-user-form-label">Last Name</span>
                <input
                  className="new-form-input-name"
                  type="text"
                  value={formData.lastName}
                  placeholder="Enter last name"
                  onChange={handleInputChange('lastName')}
                />
              </div>
            </div>

            <div className="new-user-form-row">
              <span className="new-user-form-label">Display Name:</span>
              <input
                className="new-user-form-input"
                type="text"
                value={formData.displayName}
                placeholder="Enter display name"
                onChange={handleInputChange('displayName')}
              />
            </div>

            <div className="new-user-form-row">
              <span className="new-user-form-label">Email:</span>
              <input
                className="new-user-form-input"
                type="email"
                value={formData.userEmail}
                placeholder="Enter email address"
                onChange={handleInputChange('userEmail')}
              />
            </div>

            <div className="new-user-form-row">
              <span className="new-user-form-label">Phone:</span>
              <input
                className="new-user-form-input"
                type="tel"
                value={formData.phone || ''}
                placeholder="Enter phone number (optional)"
                onChange={handleInputChange('phone')}
              />
            </div>

            {getSelectedRoleName() === 'patient' && (
              <>
                <div className="new-user-form-row">
                  <span className="new-user-form-label">Company ID:</span>
                  <input
                    className="new-user-form-input"
                    type="number"
                    value={formData.companyId}
                    placeholder="Enter company ID"
                    onChange={handleInputChange('companyId')}
                  />
                </div>

                <div className="new-user-form-row">
                  <span className="new-user-form-label">Form ID:</span>
                  <input
                    className="new-user-form-input"
                    type="number"
                    value={formData.formId}
                    placeholder="Enter form ID"
                    onChange={handleInputChange('formId')}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <div className="new-user-form-header new-user-submit-btns">
          <button onClick={handleCloseFormPressed} className="new-user-cancel">
            CANCEL
          </button>
          <button onClick={handleSubmitPressed} className="new-user-submit">
            SAVE & ADD
          </button>
        </div>
      </div>
    </div>
  );
}

export default NewUserForm;
